import React, { useEffect, useState, useRef } from 'react';
import { Button } from '@arnsbogroup/react-voicearchive'
import HandoversWidget from './components/HandoversWidget';
import AutomatedOrdersWidget from './components/AutomatedOrdersWidget';
import MyOrderWidget from './components/MyOrderWidget';
import Announcement from '../../rest/views/Dashboard/Announcement';
import MyActionTaskWidget from './components/MyActionTaskWidget';
import MyBookingsWidget from './components/MyBookingsWidget';
import OrderListWidget from './components/OrderListWidget';
import ReleasedNotDownloadedTasksWidget from './components/ReleasedNotDownloadedTasksWidget';
import NonAvailableSuppliersWidget from './components/NonAvailableSuppliersWidget';
import MyLatestViewedWidget from './components/MyLatestViewedWidget';
import MissedPaymentTasksWidget from './components/MissedPaymentTasksWidget';
import WidgetFrame from './components/WidgetFrame';
import DashboardColumn from './components/DashboardColumn';
import ColumnLayoutPicker from './components/ColumnLayoutPicker';
import AddWidgetModal from './components/AddWidgetModal';
import ResetLayoutModal from './components/ResetLayoutModal';

const COLUMN_COUNT = 3;

// Tailwind's JIT scanner only matches complete class strings in the source, so
// every width is written out - `md:w-${n}/12` would emit no CSS at all.
const COLUMN_WIDTH_CLASSES = {
  'equal':       ['md:w-1/3', 'md:w-1/3', 'md:w-1/3'],
  'wide-left':   ['md:w-1/2', 'md:w-1/4', 'md:w-1/4'],
  'wide-middle': ['md:w-1/4', 'md:w-1/2', 'md:w-1/4'],
  'wide-right':  ['md:w-1/4', 'md:w-1/4', 'md:w-1/2'],
};

// OrderListWidget is generic - it renders whatever `coordinatordashboard/{widgetId}`
// returns under data.orders - so one component serves all of these.
const ORDER_LIST_WIDGET_IDS = [
  'Closed_But_Not_Invoiced_Orders',
  'Latest_Orders',
  'Ready_For_Invoicing',
  'Orders_under_surveillance',
  'Close_To_Deadline',
  'Passed_Deadline',
];

// ...but only these honour ?scope=my|all on the backend. Adding an id here
// without teaching its controller method about the scope gives the coordinator
// a filter that changes nothing.
const SCOPE_FILTER_WIDGET_IDS = [
  'Closed_But_Not_Invoiced_Orders',
];

// Every branch here needs a matching entry in
// widget::getAvailableCoordinatorWidgets() or it can never be added.
const renderWidget = (widget) => {
  if (widget.widgetId == 'Handovers') return <HandoversWidget widget={widget} />
  if (widget.widgetId == 'My_Action_Tasks') return <MyActionTaskWidget widget={widget} />
  if (widget.widgetId == 'My_Bookings') return <MyBookingsWidget widget={widget} />
  if (widget.widgetId == 'Released_Not_Downloaded_Tasks') return <ReleasedNotDownloadedTasksWidget widget={widget} />
  if (widget.widgetId == 'My_Orders') return <MyOrderWidget widget={widget} />
  if (widget.widgetId == 'Automated_Orders') return <AutomatedOrdersWidget widget={widget} />
  if (widget.widgetId == 'Non_Available_Suppliers') return <NonAvailableSuppliersWidget widget={widget} />
  if (widget.widgetId == 'My_Latest_Viewed') return <MyLatestViewedWidget widget={widget} />
  if (widget.widgetId == 'Missed_Payment_Tasks') return <MissedPaymentTasksWidget widget={widget} />

  if (ORDER_LIST_WIDGET_IDS.includes(widget.widgetId)) {
    return (
      <OrderListWidget
        widget={widget}
        scopeFilter={SCOPE_FILTER_WIDGET_IDS.includes(widget.widgetId)}
      />
    )
  }

  // Every id in widget::getAvailableCoordinatorWidgets() has a branch above, so
  // reaching here means a *saved* layout is holding an id the backend no longer
  // serves - a widget that was renamed or removed without a migration over
  // dashboardwidgets. The generic Widget would fetch its dead endpoint and print
  // the raw "Controller method not found" body into the dashboard; say what is
  // actually wrong and how to clear it instead.
  return (
    <div className="rounded border border-gray-400 bg-white shadow">
      <div className="border-b px-3 py-2 font-medium">
        {widget.widgetId.replace(/_/g, ' ')}
      </div>
      <p className="p-4 text-xs text-gray-500">
        This widget is no longer available. Use Customize to remove it, or Reset to
        default.
      </p>
    </div>
  )
}

const emptyColumns = () => Array.from({ length: COLUMN_COUNT }, () => []);

// The API speaks a flat list carrying columnId + sorting; the page works in
// columns because that is what a coordinator arranges.
const toColumns = (list) => {
  const columns = emptyColumns();

  (Array.isArray(list) ? list : []).forEach((widget) => {
    const index = Math.min(Math.max((widget.columnId || 1) - 1, 0), COLUMN_COUNT - 1);
    columns[index].push(widget);
  });

  return columns.map((column) =>
    [...column].sort((a, b) => (a.sorting || 0) - (b.sorting || 0))
  );
};

const toPayload = (columns) =>
  columns.flatMap((column, columnIndex) =>
    column.map((widget, sorting) => ({
      widgetId: widget.widgetId,
      columnId: columnIndex + 1,
      sorting,
    }))
  );

const serialize = (columns, columnLayout) =>
  JSON.stringify({ columnLayout, widgets: toPayload(columns) });

const moveWidget = (columns, fromColumn, fromIndex, toColumn, toIndex) => {
  const next = columns.map((column) => [...column]);
  const [widget] = next[fromColumn].splice(fromIndex, 1);

  if (!widget) return columns;

  next[toColumn].splice(toIndex, 0, widget);
  return next;
};

const CoordinatorDashboardPage = () => {
  const mounted = useRef(false);

  const [columns, setColumns] = useState(emptyColumns);
  const [columnLayout, setColumnLayout] = useState('equal');
  const [columnLayouts, setColumnLayouts] = useState([]);
  const [available, setAvailable] = useState([]);
  const [customizing, setCustomizing] = useState(false);
  const [baseline, setBaseline] = useState('');
  const [dropTarget, setDropTarget] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState('');
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    mounted.current = true;
    document.title = 'Dashboard';
    fetchWidgets({ clearFirst: true });
    fetchSettings();

    return () => {
      mounted.current = false;
    };
  }, []);

  // GET /rest/v2/widget returns a bare array (the legacy contract all three
  // dashboards consume), not a ResponseBody envelope.
  //
  // clearFirst is only for the Reload button, where emptying the grid is the
  // point. Everywhere else the list is replaced in place so widgets that survive
  // the change keep their already-fetched data instead of remounting.
  const fetchWidgets = ({ clearFirst = false } = {}) => {
    if (clearFirst) setColumns(emptyColumns());

    return fetch('/rest/v2/widget', { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((data) => {
        if (mounted.current) setColumns(toColumns(data));
      });
  };

  const fetchSettings = () =>
    fetch('/rest/v2/widget/settings', { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((body) => {
        if (mounted.current && body.data) {
          setColumnLayout(body.data.columnLayout || 'equal');
          setColumnLayouts(body.data.columnLayouts || []);
        }
      });

  const fetchAvailable = () =>
    fetch('/rest/v2/widget/available', { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((body) => {
        if (mounted.current) setAvailable(body.data || []);
      });

  const labelFor = (widgetId) => {
    const entry = available.find((option) => option.widgetId === widgetId);
    return entry ? entry.label : widgetId.replace(/_/g, ' ');
  };

  const flat = columns.flat();
  const total = flat.length;
  const dirty = customizing && serialize(columns, columnLayout) !== baseline;
  const widthClasses = COLUMN_WIDTH_CLASSES[columnLayout] || COLUMN_WIDTH_CLASSES.equal;

  const announceMove = (widget, toColumn, toIndex) =>
    setAnnouncement(
      `${labelFor(widget.widgetId)} moved to column ${toColumn + 1}, position ${toIndex + 1}`
    );

  // Up/down move within a column, left/right between columns. Moving sideways
  // keeps the widget at roughly the same height rather than dropping it to the
  // bottom of the target column.
  const onArrow = (columnIndex, index, direction) => {
    const widget = columns[columnIndex][index];
    if (!widget) return;

    let toColumn = columnIndex;
    let toIndex = index;

    if (direction === 'up') toIndex = index - 1;
    if (direction === 'down') toIndex = index + 1;

    if (direction === 'left' || direction === 'right') {
      toColumn = direction === 'left' ? columnIndex - 1 : columnIndex + 1;
      toIndex = Math.min(index, columns[toColumn].length);
    }

    setColumns((current) => moveWidget(current, columnIndex, index, toColumn, toIndex));
    announceMove(widget, toColumn, toIndex);
  };

  const onDropWidget = (item, toColumn, toIndex) => {
    const widget = columns[item.columnIndex] && columns[item.columnIndex][item.index];
    setDropTarget(null);

    if (!widget) return;
    if (item.columnIndex === toColumn && item.index === toIndex) return;

    setColumns((current) => moveWidget(current, item.columnIndex, item.index, toColumn, toIndex));
    announceMove(widget, toColumn, toIndex);
  };

  const onHover = (columnIndex, index) => {
    setDropTarget((current) =>
      current && current.columnIndex === columnIndex && current.index === index
        ? current
        : { columnIndex, index }
    );
  };

  const onRemove = (columnIndex, index) => {
    const widget = columns[columnIndex][index];
    setColumns((current) =>
      current.map((column, i) => (i === columnIndex ? column.filter((_, j) => j !== index) : column))
    );
    setAnnouncement(`${labelFor(widget.widgetId)} removed`);
  };

  // Lands in whichever column currently has the fewest widgets, so adding never
  // buries it at the bottom of an already long column.
  const onAdd = (entry) => {
    const target = columns.reduce(
      (shortest, column, i) => (column.length < columns[shortest].length ? i : shortest),
      0
    );

    setColumns((current) =>
      current.map((column, i) => (i === target ? [...column, { widgetId: entry.widgetId }] : column))
    );
    setAnnouncement(`${entry.label} added to column ${target + 1}`);
  };

  const onCustomize = () => {
    setError('');
    setBaseline(serialize(columns, columnLayout));
    setCustomizing(true);
    fetchAvailable();
  };

  const leaveCustomizing = () => {
    setCustomizing(false);
    setShowAdd(false);
    setShowReset(false);
    setDropTarget(null);
    setError('');
  };

  const onSave = () => {
    setSaving(true);
    setError('');

    fetch('/rest/v2/widget', {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ columnLayout, widgets: toPayload(columns) }),
    })
      .then((response) => response.json())
      .then((body) => {
        if (!mounted.current) return;

        if (body && body.success === false) {
          setError((body.errors || ['Your layout could not be saved.']).join(' '));
          return;
        }

        leaveCustomizing();
        setAnnouncement('Dashboard layout saved');
        return fetchWidgets();
      })
      .catch(() => {
        if (mounted.current) setError('Your layout could not be saved. Check your connection and try again.');
      })
      .finally(() => {
        if (mounted.current) setSaving(false);
      });
  };

  const onReset = () => {
    setResetting(true);
    setError('');

    fetch('/rest/v2/widget', { method: 'DELETE', credentials: 'same-origin' })
      .then(() => {
        if (!mounted.current) return;
        leaveCustomizing();
        setAnnouncement('Dashboard reset to the default layout');
        return Promise.all([fetchWidgets(), fetchSettings()]);
      })
      .catch(() => {
        if (mounted.current) setError('Your dashboard could not be reset. Check your connection and try again.');
      })
      .finally(() => {
        if (mounted.current) setResetting(false);
      });
  };

  const onCancel = () => {
    leaveCustomizing();
    setAnnouncement('Changes discarded');
    fetchWidgets();
    fetchSettings();
  };

  return (
    <div className="mainDashboard flex flex-col w-full h-full antialiased text-sm">

      <Announcement />

      <div aria-live="polite" className="sr-only">{announcement}</div>

      <div className="w-full pt-1 px-5 flex flex-wrap gap-2 justify-between items-center text-sm">
        {!customizing ? (
          <>
            <Button
              variant="inline"
              onClick={() => fetchWidgets({ clearFirst: true })}
              icon={['fa', 'redo-alt']}
              iconPosition="right"
            >
              Reload
            </Button>

            <Button
              variant="inline"
              title="Customize"
              onClick={onCustomize}
              icon={['fa', 'cog']}
              iconPosition="right"
            >
              Customize
            </Button>
          </>
        ) : (
          <>
            <span className="flex flex-wrap items-center gap-3">
              <span className="font-medium text-gray-700">Editing layout</span>
              {dirty && (
                <span className="rounded-full bg-warning px-2 py-px text-xs font-medium text-white">
                  Unsaved changes
                </span>
              )}
              <span className="hidden items-center gap-2 md:flex">
                <span className="text-xs text-gray-500">Column widths</span>
                <ColumnLayoutPicker
                  layouts={columnLayouts}
                  value={columnLayout}
                  onChange={setColumnLayout}
                />
              </span>
            </span>

            <span className="flex flex-wrap items-center gap-2">
              {/* Destructive and immediate, so it is kept away from Save. */}
              <Button variant="text" state="negative" size="sm" onClick={() => setShowReset(true)}>
                Reset to default
              </Button>

              <span className="mx-1 hidden h-6 w-px bg-gray-400 sm:block" aria-hidden="true" />

              <Button variant="outline" size="sm" icon={['fa', 'plus']} onClick={() => setShowAdd(true)}>
                Add widget
              </Button>
              <Button variant="outline" size="sm" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                variant="solid"
                state="positive"
                size="sm"
                disabled={saving || !dirty}
                title={dirty ? 'Save your layout' : 'No changes to save'}
                onClick={onSave}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </span>
          </>
        )}
      </div>

      {customizing && (
        <p className="px-5 pt-2 text-xs text-gray-500">
          Drag a widget by its handle to any position in any column, or use the arrows to
          move it up, down, or into the next column.
        </p>
      )}

      {!!error && (
        <div role="alert" className="mx-5 mt-2 rounded border border-negative bg-white px-3 py-2 text-negative">
          {error}
        </div>
      )}

      <div className={`grow ${customizing ? 'mt-2 border-t bg-gray-50' : ''}`}>
        {/* Three independent stacks, not one grid. Grid rows share a height, so
            a 25,000px widget would strand everything beside it 25,000px down the
            page; separate columns let each one stack on its own. */}
        <div className="flex flex-col items-start gap-4 p-4 md:flex-row">
          {columns.map((column, columnIndex) => (
            <DashboardColumn
              key={columnIndex}
              columnIndex={columnIndex}
              widthClass={widthClasses[columnIndex]}
              count={column.length}
              customizing={customizing}
              dropIndex={dropTarget && dropTarget.columnIndex === columnIndex ? dropTarget.index : null}
              onHover={onHover}
              onDrop={onDropWidget}
            >
              {column.map((widget, index) => (
                // Keyed by widgetId, never by index - keying by index would
                // remount every widget on each reorder and re-fire its fetch.
                <WidgetFrame
                  key={widget.widgetId}
                  widget={widget}
                  label={labelFor(widget.widgetId)}
                  columnIndex={columnIndex}
                  index={index}
                  customizing={customizing}
                  canRemove={total > 1}
                  canMoveUp={index > 0}
                  canMoveDown={index < column.length - 1}
                  canMoveLeft={columnIndex > 0}
                  canMoveRight={columnIndex < COLUMN_COUNT - 1}
                  onArrow={onArrow}
                  onRemove={onRemove}
                  onDragEnd={() => setDropTarget(null)}
                >
                  {renderWidget(widget)}
                </WidgetFrame>
              ))}
            </DashboardColumn>
          ))}
        </div>
      </div>

      {/* Only mounted while customizing: Modal registers its Esc/Enter shortcuts
          unconditionally, so leaving them mounted would swallow those keys on
          the dashboard itself. */}
      {customizing && (
        <>
          <AddWidgetModal
            show={showAdd}
            available={available}
            widgets={flat}
            onAdd={onAdd}
            onClose={() => setShowAdd(false)}
          />
          <ResetLayoutModal
            show={showReset}
            resetting={resetting}
            onConfirm={onReset}
            onClose={() => setShowReset(false)}
          />
        </>
      )}

    </div>
  );
}

export default CoordinatorDashboardPage;
